import React, { useEffect, useRef, useState } from "react";
import { useActionItemsHeaderHoverMenu } from "./useActionItemsHeaderHoverMenu";

const MENU_CLOSE_DELAY_MS = 120;
/** Grace period before closing a submenu when crossing non-submenu rows (e.g. AI Assist). */
const SUBMENU_HOVER_CLOSE_DELAY_MS = 280;
/** Grace period before switching Source ↔ Categories so diagonal moves stay on the same subpanel. */
const SUBMENU_SWITCH_DELAY_MS = 200;

const FunnelIcon = () => (
  <svg
    className="action-items-filter-hover-menu__icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    aria-hidden="true"
  >
    <path d="M4 5h16l-6.2 7.4v5.1l-3.6 2.1v-7.2L4 5z" strokeLinejoin="round" />
  </svg>
);

const findOptionLabel = (options, value) =>
  (options || []).find((option) => option.value === value)?.label || "";

/**
 * Hover flyout for Source / Category filters on the action-items header.
 * Sub-options open in a side panel to the left; only one subpanel is visible at a time.
 *
 * @example
 * // Input — Zoom selected, two category options
 * <ActionItemsFilterHoverMenu
 *   sourceFilter={{ value: "wf-zoom", options: [{ value: "", label: "All tasks" }, ...], onChange }}
 *   categoryFilter={{ value: "__all__", options: [...], onChange }}
 *   filtersActive
 * />
 * // Output — funnel button with dot when active; hover shows Source › and Categories › rows
 */
const ActionItemsFilterHoverMenu = ({
  sourceFilter,
  categoryFilter,
  aiReadyFilterActive = false,
  onToggleAiReadyFilter,
  agingFilterActive = false,
  onToggleAgingFilter,
  starredFilterActive = false,
  onToggleStarredFilter,
  onActivateDateFilter,
  dateFilterActive = false,
  filtersActive = false,
  menuLabel = "Filter action items",
}) => {
  const { menuOpen, openMenu, closeMenu, handleMouseLeave } =
    useActionItemsHeaderHoverMenu("filter");
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const closeTimerRef = useRef(null);
  const submenuCloseTimerRef = useRef(null);
  const submenuSwitchTimerRef = useRef(null);
  const openSubmenuRef = useRef(null);

  useEffect(() => {
    openSubmenuRef.current = openSubmenu;
  }, [openSubmenu]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (submenuCloseTimerRef.current) clearTimeout(submenuCloseTimerRef.current);
      if (submenuSwitchTimerRef.current) clearTimeout(submenuSwitchTimerRef.current);
    },
    []
  );

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const clearSubmenuCloseTimer = () => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  };

  const clearSubmenuSwitchTimer = () => {
    if (submenuSwitchTimerRef.current) {
      clearTimeout(submenuSwitchTimerRef.current);
      submenuSwitchTimerRef.current = null;
    }
  };

  const scheduleCloseMenu = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      clearSubmenuCloseTimer();
      clearSubmenuSwitchTimer();
      setOpenSubmenu(null);
      handleMouseLeave();
    }, MENU_CLOSE_DELAY_MS);
  };

  const handleRootMouseEnter = () => {
    clearCloseTimer();
    clearSubmenuCloseTimer();
    openMenu();
  };

  const showSource = Boolean(sourceFilter?.options?.length);
  const showCategory = Boolean(categoryFilter?.options?.length);
  const showAiReady = typeof onToggleAiReadyFilter === "function";
  const showAging = typeof onToggleAgingFilter === "function";
  const showStarred = typeof onToggleStarredFilter === "function";
  const showEventDate = typeof onActivateDateFilter === "function";
  if (!showSource && !showCategory && !showAiReady && !showAging && !showStarred && !showEventDate) {
    return null;
  }

  const sourceValueLabel = showSource
    ? findOptionLabel(sourceFilter.options, sourceFilter.value)
    : "";
  const categoryValueLabel = showCategory
    ? findOptionLabel(categoryFilter.options, categoryFilter.value)
    : "";

  const toggleClass = [
    "action-items-filter-hover-menu__toggle",
    "action-items-header-icon-btn",
    filtersActive ? "action-items-filter-hover-menu__toggle--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const ariaLabel = filtersActive ? `${menuLabel} (filters active)` : menuLabel;

  const handleRootMouseLeave = () => {
    scheduleCloseMenu();
  };

  const keepSubmenuOpen = (submenuKey) => {
    clearCloseTimer();
    clearSubmenuCloseTimer();
    clearSubmenuSwitchTimer();

    const current = openSubmenuRef.current;
    if (current === submenuKey) return;

    if (!current) {
      setOpenSubmenu(submenuKey);
      return;
    }

    submenuSwitchTimerRef.current = setTimeout(() => {
      setOpenSubmenu(submenuKey);
      submenuSwitchTimerRef.current = null;
    }, SUBMENU_SWITCH_DELAY_MS);
  };

  const scheduleCloseSubmenu = () => {
    clearSubmenuCloseTimer();
    submenuCloseTimerRef.current = setTimeout(() => {
      setOpenSubmenu(null);
      submenuCloseTimerRef.current = null;
    }, SUBMENU_HOVER_CLOSE_DELAY_MS);
  };

  const closeSubmenu = () => {
    clearCloseTimer();
    clearSubmenuCloseTimer();
    clearSubmenuSwitchTimer();
    setOpenSubmenu(null);
  };

  const renderOptionList = (filter, title, submenuKey) => {
    if (openSubmenu !== submenuKey) return null;
    const options = filter.options || [];
    // Long connector names wrap and push later rows (Slack sorts after Granola)
    // below the fold; mark overflow so the list stays scrollable with a visible bar.
    const scrollable = options.length > 6;

    return (
      <div
        className={[
          "action-items-filter-hover-menu__subpanel",
          "action-items-filter-hover-menu__subpanel--open",
          scrollable ? "action-items-filter-hover-menu__subpanel--scrollable" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="menu"
        aria-label={title}
        onMouseEnter={() => keepSubmenuOpen(submenuKey)}
      >
        {options.map(({ value, label }) => {
          const selected = filter.value === value;
          return (
            <button
              key={value || "__all__"}
              type="button"
              className={`action-items-filter-hover-menu__option${
                selected ? " action-items-filter-hover-menu__option--selected" : ""
              }`}
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => filter.onChange?.(value)}
              title={label}
            >
              <span className="action-items-filter-hover-menu__option-label">{label}</span>
              {selected ? (
                <span className="action-items-filter-hover-menu__check" aria-hidden="true">
                  ✓
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  };

  const submenuRowClass = (submenuKey) =>
    [
      "action-items-filter-hover-menu__submenu-row",
      openSubmenu === submenuKey ? "action-items-filter-hover-menu__submenu-row--open" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const panelClass = [
    "action-items-filter-hover-menu__panel",
    openSubmenu ? "action-items-filter-hover-menu__panel--submenu-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const rootClass = [
    "action-items-filter-hover-menu",
    menuOpen ? "action-items-filter-hover-menu--open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      onMouseEnter={handleRootMouseEnter}
      onMouseLeave={handleRootMouseLeave}
      onFocus={handleRootMouseEnter}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          clearCloseTimer();
          clearSubmenuCloseTimer();
          clearSubmenuSwitchTimer();
          setOpenSubmenu(null);
          closeMenu();
        }
      }}
    >
      <button
        type="button"
        className={toggleClass}
        aria-haspopup="true"
        aria-expanded={menuOpen}
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        <FunnelIcon />
      </button>
      <div className={panelClass} role="menu" aria-label="Filter by">
        <div className="action-items-filter-hover-menu__label">Filter by</div>
        {showSource ? (
          <div
            className={submenuRowClass("source")}
            onMouseEnter={() => keepSubmenuOpen("source")}
          >
            <button
              type="button"
              className="action-items-filter-hover-menu__row"
              role="menuitem"
              onClick={() => keepSubmenuOpen("source")}
            >
              <span>Source</span>
              <span className="action-items-filter-hover-menu__row-right">
                {sourceValueLabel ? (
                  <span className="action-items-filter-hover-menu__row-value">
                    {sourceValueLabel}
                  </span>
                ) : null}
                <span className="action-items-filter-hover-menu__chevron" aria-hidden="true">
                  ›
                </span>
              </span>
            </button>
            {renderOptionList(sourceFilter, "Source", "source")}
          </div>
        ) : null}
        {showCategory ? (
          <div
            className={submenuRowClass("category")}
            onMouseEnter={() => keepSubmenuOpen("category")}
          >
            <button
              type="button"
              className="action-items-filter-hover-menu__row"
              role="menuitem"
              onClick={() => keepSubmenuOpen("category")}
            >
              <span>Categories</span>
              <span className="action-items-filter-hover-menu__row-right">
                {categoryValueLabel ? (
                  <span className="action-items-filter-hover-menu__row-value">
                    {categoryValueLabel}
                  </span>
                ) : null}
                <span className="action-items-filter-hover-menu__chevron" aria-hidden="true">
                  ›
                </span>
              </span>
            </button>
            {renderOptionList(categoryFilter, "Categories", "category")}
          </div>
        ) : null}
        {showAiReady ? (
          <button
            type="button"
            className={`action-items-filter-hover-menu__row action-items-filter-hover-menu__row--checkable${
              aiReadyFilterActive ? " action-items-filter-hover-menu__row--selected" : ""
            }`}
            role="menuitemcheckbox"
            aria-checked={aiReadyFilterActive}
            onMouseEnter={scheduleCloseSubmenu}
            onClick={() => {
              closeSubmenu();
              onToggleAiReadyFilter();
            }}
          >
            <span>AI Assist only</span>
            {aiReadyFilterActive ? (
              <span className="action-items-filter-hover-menu__check" aria-hidden="true">
                ✓
              </span>
            ) : null}
          </button>
        ) : null}
        {showStarred ? (
          <button
            type="button"
            className={`action-items-filter-hover-menu__row action-items-filter-hover-menu__row--checkable${
              starredFilterActive ? " action-items-filter-hover-menu__row--selected" : ""
            }`}
            role="menuitemcheckbox"
            aria-checked={starredFilterActive}
            onMouseEnter={scheduleCloseSubmenu}
            onClick={() => {
              closeSubmenu();
              onToggleStarredFilter();
            }}
          >
            <span>Starred</span>
            {starredFilterActive ? (
              <span className="action-items-filter-hover-menu__check" aria-hidden="true">
                ✓
              </span>
            ) : null}
          </button>
        ) : null}
        {showAging ? (
          <button
            type="button"
            className={`action-items-filter-hover-menu__row action-items-filter-hover-menu__row--checkable${
              agingFilterActive ? " action-items-filter-hover-menu__row--selected" : ""
            }`}
            role="menuitemcheckbox"
            aria-checked={agingFilterActive}
            onMouseEnter={scheduleCloseSubmenu}
            onClick={() => {
              closeSubmenu();
              onToggleAgingFilter();
            }}
          >
            <span>Aging</span>
            {agingFilterActive ? (
              <span className="action-items-filter-hover-menu__check" aria-hidden="true">
                ✓
              </span>
            ) : null}
          </button>
        ) : null}
        {showEventDate ? (
          <button
            type="button"
            className={`action-items-filter-hover-menu__row action-items-filter-hover-menu__row--checkable${
              dateFilterActive ? " action-items-filter-hover-menu__row--selected" : ""
            }`}
            role="menuitemcheckbox"
            aria-checked={dateFilterActive}
            onMouseEnter={scheduleCloseSubmenu}
            onClick={() => {
              closeSubmenu();
              if (!dateFilterActive) onActivateDateFilter();
            }}
          >
            <span>Event date</span>
            {dateFilterActive ? (
              <span className="action-items-filter-hover-menu__check" aria-hidden="true">
                ✓
              </span>
            ) : null}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default ActionItemsFilterHoverMenu;
